import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Grid from '@cloudscape-design/components/grid';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { SessionTimer, SpeakingIndicator } from '../components';
import type { SpeakerState } from '../components/SpeakingIndicator';
import { livekitService, ConnectionState } from '../services/livekit';

/** Default session duration in minutes */
const SESSION_DURATION_MINUTES = 10;

/** Countdown duration in seconds before session starts */
const COUNTDOWN_SECONDS = 3;

/**
 * Pre-call briefing configuration.
 * Returns AM info and script for the scenario-specific pre-call huddle.
 */
interface PreCallConfig {
  amName: string;
  amTitle: string;
  amPhotoUrl: string;
  audioUrl: string | null;
  script: string;
}

function getPreCallConfig(scenarioId: string, role: string, scenarioName: string): PreCallConfig {
  // SecureWealth has a full audio briefing from Steve
  if (scenarioId === 'SecureWealth_Financial') {
    return {
      amName: 'Steve Simion',
      amTitle: 'FSI Sales Manager',
      amPhotoUrl: '/avatars/am-steve-simion.jpg',
      audioUrl: '/audio/am-brief-SecureWealth_Financial.mp3',
      script: `Hey, appreciate you jumping on this one. Sarah had a conflict so you're covering. You're meeting with the CDO at SecureWealth Financial, an 850 million dollar wealth management firm. He's sharp, knows the tech landscape, but he's laser-focused on growth targets the board set. They need to modernize but compliance is non-negotiable. I sent you some notes to review. You got this.`,
    };
  }

  // Default: generic brief with no audio (text-only for other scenarios)
  return {
    amName: 'Steve Simion',
    amTitle: 'FSI Sales Manager',
    amPhotoUrl: '/avatars/am-steve-simion.jpg',
    audioUrl: null,
    script: `Hey, appreciate you covering this call. You're meeting with the ${role} at ${scenarioName}. I sent you the briefing notes — take a minute to review before you jump in. Focus on discovery first. Good luck.`,
  };
}

interface LocationState {
  scenarioId?: string;
  scenarioName?: string;
  role?: string;
  industry?: string;
  description?: string;
}

/**
 * VoiceSessionPage Component
 *
 * Active voice training session interface with:
 * - 3-second countdown before session begins
 * - Role player avatar displayed like a video conference participant
 * - Collapsible case study brief panel for reference during the call
 * - Real LiveKit voice integration
 */
export function VoiceSessionPage(): React.ReactElement {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Connection state
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Countdown state
  const [countdown, setCountdown] = useState<number | null>(null);
  const [showCountdown, setShowCountdown] = useState(false);

  // Pre-call briefing state — only show when audio is available for this scenario
  const [showPreCallBrief, setShowPreCallBrief] = useState(false);

  // Session state
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [currentSpeaker, setCurrentSpeaker] = useState<SpeakerState>('none');
  const [isSessionActive, setIsSessionActive] = useState<boolean>(false);
  const [sessionStarted, setSessionStarted] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Brief panel state
  const [briefHtml, setBriefHtml] = useState<string | null>(null);
  const [briefExpanded, setBriefExpanded] = useState(true);
  const briefRef = useRef<HTMLDivElement>(null);

  // Scenario info from navigation state or URL
  const scenarioName = state?.scenarioName || scenarioId || 'Unknown';
  const role = state?.role || 'Unknown Role';

  // Avatar URL
  const avatarUrl = scenarioId ? `/avatars/${scenarioId}.png` : null;

  // Pre-call briefing config per scenario
  const preCallConfig = getPreCallConfig(scenarioId || '', role, scenarioName);

  // Only show pre-call briefing for scenarios that have audio
  useEffect(() => {
    if (preCallConfig.audioUrl) {
      setShowPreCallBrief(true);
    }
  }, [preCallConfig.audioUrl]);

  /**
   * Fetch the case study brief HTML for the current scenario.
   */
  useEffect(() => {
    if (!scenarioId) return;

    fetch(`/scenario-briefs/${scenarioId}.html`)
      .then((res) => {
        if (res.ok) return res.text();
        return null;
      })
      .then((html) => {
        if (html) {
          // Extract body content from the HTML document
          const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
          const styles = styleMatch ? styleMatch.join('\n') : '';
          const body = bodyMatch ? bodyMatch[1] : html;
          setBriefHtml(styles + body);
        }
      })
      .catch(() => {
        setBriefHtml(null);
      });
  }, [scenarioId]);

  /**
   * Connect to LiveKit room and start countdown.
   * Only runs after pre-call briefing is dismissed.
   */
  useEffect(() => {
    if (showPreCallBrief) return; // Wait for pre-call to finish

    let cancelled = false;

    const connectToSession = async () => {
      if (!scenarioId) {
        setError('No scenario selected. Please go back and select a scenario.');
        return;
      }

      setIsConnecting(true);
      setError(null);

      try {
        // Create session via API
        const session = await livekitService.createSession(
          scenarioId,
          scenarioName,
          role
        );

        if (cancelled) return;

        // Store the session ID for evaluation lookup
        setSessionId(session.sessionId);

        // Connect to LiveKit room
        await livekitService.connect(session.serverUrl, session.token);

        if (cancelled) return;

        // Connection established — start countdown
        setIsConnecting(false);
        setShowCountdown(true);
        setCountdown(COUNTDOWN_SECONDS);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Failed to connect:', err);
        setError(err.message || 'Failed to connect to session. Please try again.');
        setIsConnecting(false);
      }
    };

    connectToSession();

    // Cleanup on unmount
    return () => {
      cancelled = true;
      livekitService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, showPreCallBrief]);

  /**
   * Countdown timer effect.
   */
  useEffect(() => {
    if (!showCountdown || countdown === null) return;

    if (countdown <= 0) {
      // Countdown finished — activate session
      setShowCountdown(false);
      setIsSessionActive(true);
      setSessionStarted(true);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [showCountdown, countdown]);

  /**
   * Set up LiveKit event listeners.
   */
  useEffect(() => {
    const unsubscribeSpeaker = livekitService.onSpeakerChange((speaker) => {
      setCurrentSpeaker(speaker);
    });

    const unsubscribeConnection = livekitService.onConnectionStateChange((state) => {
      setConnectionState(state);
      if (state === 'failed') {
        setError('Connection lost. Please try reconnecting.');
      }
    });

    const unsubscribeError = livekitService.onError((err) => {
      if (!isSessionActive) {
        setError(err.message);
      } else {
        console.warn('LiveKit error during active session:', err.message);
      }
    });

    return () => {
      unsubscribeSpeaker();
      unsubscribeConnection();
      unsubscribeError();
    };
  }, []);

  /**
   * Handle session end - navigates to evaluation page.
   */
  const handleSessionEnd = useCallback((): void => {
    if (!isSessionActive) return;

    setIsSessionActive(false);
    livekitService.disconnect();

    const evalSessionId = sessionId || `session-${scenarioId}-${Date.now()}`;
    navigate(`/evaluation/${evalSessionId}`);
  }, [isSessionActive, sessionId, scenarioId, navigate]);

  const handleTimeUp = useCallback((): void => {
    handleSessionEnd();
  }, [handleSessionEnd]);

  const handleMuteToggle = useCallback((): void => {
    const newMutedState = livekitService.toggleMute();
    setIsMuted(newMutedState);
  }, []);

  const handleEndSessionClick = useCallback((): void => {
    handleSessionEnd();
  }, [handleSessionEnd]);

  const handleRetry = useCallback((): void => {
    window.location.reload();
  }, []);

  const handleGoBack = useCallback((): void => {
    navigate('/scenarios');
  }, [navigate]);

  // Pre-call ringing phase (auto-accept after 2s)
  const [preCallPhase, setPreCallPhase] = useState<'ringing' | 'briefing'>('ringing');

  useEffect(() => {
    if (!showPreCallBrief) return;
    const timer = setTimeout(() => setPreCallPhase('briefing'), 2000);
    return () => clearTimeout(timer);
  }, [showPreCallBrief]);

  // ==================== PRE-CALL INCOMING CALL ====================
  if (showPreCallBrief && preCallPhase === 'ringing') {
    return (
      <ContentLayout header={<Header variant="h1">Incoming Call...</Header>}>
        <Container>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            padding: '60px 20px',
          }}>
            <div style={{ position: 'relative', width: 120, height: 120 }}>
              <div className="ring ring-1" />
              <div className="ring ring-2" />
              <div className="ring ring-3" />
              <div style={{
                position: 'absolute', top: 0, left: 0,
                width: 120, height: 120, borderRadius: '50%',
                overflow: 'hidden', border: '3px solid #22c55e', zIndex: 2,
              }}>
                <img src={preCallConfig.amPhotoUrl} alt={preCallConfig.amName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{preCallConfig.amName}</div>
              <div style={{ fontSize: '13px', color: '#5f6b7a', marginTop: '2px' }}>{preCallConfig.amTitle}</div>
            </div>
            <div style={{ fontSize: '14px', color: '#5f6b7a', animation: 'fadeInOut 1.5s infinite' }}>
              Calling you...
            </div>
          </div>
        </Container>
        <style>{`
          .ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 2px solid #22c55e; opacity: 0; animation: ringPulse 2s infinite; }
          .ring-1 { width: 140px; height: 140px; animation-delay: 0s; }
          .ring-2 { width: 165px; height: 165px; animation-delay: 0.4s; }
          .ring-3 { width: 190px; height: 190px; animation-delay: 0.8s; }
          @keyframes ringPulse { 0% { opacity: 0.6; transform: translate(-50%,-50%) scale(0.85); } 100% { opacity: 0; transform: translate(-50%,-50%) scale(1.1); } }
          @keyframes fadeInOut { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        `}</style>
      </ContentLayout>
    );
  }

  // ==================== PRE-CALL BRIEFING FROM AM ====================
  if (showPreCallBrief) {
    return (
      <ContentLayout
        header={<Header variant="h1">Pre-Call Briefing</Header>}
      >
        <Grid
          gridDefinition={[
            { colspan: { default: 12, l: 7 } },
            { colspan: { default: 12, l: 5 } },
          ]}
        >
          {/* Left: AM video call simulation */}
          <Container disableContentPaddings>
            <div style={{
              backgroundColor: '#1a1a2e',
              borderRadius: '8px',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              minHeight: '360px',
              justifyContent: 'center',
            }}>
              {/* AM photo */}
              <div style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid #22c55e',
              }}>
                <img
                  src={preCallConfig.amPhotoUrl}
                  alt={preCallConfig.amName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
                  {preCallConfig.amName}
                </div>
                <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '2px' }}>
                  {preCallConfig.amTitle}
                </div>
              </div>

              {/* Script as speech bubble */}
              <div style={{
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '12px',
                padding: '16px 20px',
                maxWidth: '480px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#e2e8f0',
                fontStyle: 'italic',
                textAlign: 'center',
              }}>
                "{preCallConfig.script}"
              </div>

              {/* Audio player - auto plays */}
              {preCallConfig.audioUrl && (
                <audio
                  autoPlay
                  src={preCallConfig.audioUrl}
                  style={{ display: 'none' }}
                />
              )}

              {/* Join Call button */}
              <Button
                variant="primary"
                onClick={() => setShowPreCallBrief(false)}
                iconName="call"
              >
                Join Customer Call
              </Button>
            </div>
          </Container>

          {/* Right: Briefing notes */}
          {briefHtml && (
            <Container
              header={
                <Header variant="h2" description="Review before joining the call">
                  Case Study Notes
                </Header>
              }
            >
              <div
                className="brief-panel-content"
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '0 4px',
                }}
                dangerouslySetInnerHTML={{ __html: briefHtml }}
              />
            </Container>
          )}
        </Grid>
      </ContentLayout>
    );
  }

  // Show loading state while connecting
  if (isConnecting) {
    return (
      <ContentLayout
        header={
          <Header variant="h1">Connecting to Session...</Header>
        }
      >
        <Container>
          <Box textAlign="center" padding="xxl">
            <SpaceBetween size="l" alignItems="center">
              <Spinner size="large" />
              <Box variant="h3">Setting up your training session</Box>
              <Box color="text-body-secondary">
                Connecting to {scenarioName} scenario as {role}...
              </Box>
            </SpaceBetween>
          </Box>
        </Container>
      </ContentLayout>
    );
  }

  // Show countdown before session begins
  if (showCountdown && countdown !== null) {
    return (
      <ContentLayout
        header={
          <Header variant="h1">Starting Session...</Header>
        }
      >
        <Container>
          <Box textAlign="center" padding="xxl">
            <SpaceBetween size="l" alignItems="center">
              {/* Avatar preview during countdown */}
              {avatarUrl && (
                <div style={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '3px solid #0972d3',
                }}>
                  <img
                    src={avatarUrl}
                    alt={`${role} avatar`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              <Box variant="h3">
                Meeting with {role} at {scenarioName}
              </Box>
              <div style={{
                fontSize: '72px',
                fontWeight: 700,
                color: '#0972d3',
                lineHeight: 1,
              }}>
                {countdown}
              </div>
              <Box color="text-body-secondary">
                Session starting in {countdown} second{countdown !== 1 ? 's' : ''}...
              </Box>
            </SpaceBetween>
          </Box>
        </Container>
      </ContentLayout>
    );
  }

  // Show error state
  if (error && !isSessionActive) {
    return (
      <ContentLayout
        header={
          <Header variant="h1">Connection Error</Header>
        }
      >
        <SpaceBetween size="l">
          <Alert type="error" header="Failed to connect">
            {error}
          </Alert>
          <Box>
            <SpaceBetween direction="horizontal" size="s">
              <Button onClick={handleRetry}>Try Again</Button>
              <Button variant="link" onClick={handleGoBack}>Back to Scenarios</Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </ContentLayout>
    );
  }

  // Main session view
  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`Training session: ${scenarioName} - You are the AWS Solutions Architect`}
        >
          Voice Session
        </Header>
      }
    >
      <Grid
        gridDefinition={[
          { colspan: { default: 12, l: briefExpanded && briefHtml ? 6 : 12 } },
          ...(briefExpanded && briefHtml ? [{ colspan: { default: 12, l: 6 } }] : []),
        ]}
      >
        {/* Left column: Session interface */}
        <SpaceBetween size="l">
          {/* Video conference-style tile with avatar */}
          <Container disableContentPaddings>
            <div style={{
              backgroundColor: '#1a1a2e',
              borderRadius: '8px',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              minHeight: '280px',
              justifyContent: 'center',
            }}>
              {/* Avatar circle */}
              {avatarUrl && (
                <div style={{
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: currentSpeaker === 'ai'
                    ? '4px solid #4ade80'
                    : '3px solid #444',
                  transition: 'border-color 0.2s ease',
                }}>
                  <img
                    src={avatarUrl}
                    alt={`${role} avatar`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              {/* Name label */}
              <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: 500 }}>
                {role}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '13px' }}>
                {scenarioName}
              </div>
              {/* Speaking indicator */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: currentSpeaker === 'ai' ? '#4ade80' : '#6b7280',
                fontSize: '13px',
              }}>
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: currentSpeaker === 'ai' ? '#4ade80' : '#6b7280',
                  display: 'inline-block',
                  animation: currentSpeaker === 'ai' ? 'pulse 1.5s infinite' : 'none',
                }} />
                {currentSpeaker === 'ai' ? 'Speaking...' : 'Listening'}
              </div>
            </div>
          </Container>

          {/* Timer and Connection status row */}
          <Grid
            gridDefinition={[
              { colspan: { default: 12, s: 6 } },
              { colspan: { default: 12, s: 6 } },
            ]}
          >
            <Container header={<Header variant="h2">Session Timer</Header>}>
              {sessionStarted ? (
                <SessionTimer
                  durationMinutes={SESSION_DURATION_MINUTES}
                  onTimeUp={handleTimeUp}
                />
              ) : (
                <Box textAlign="center" padding="l">
                  <Spinner /> Waiting to start...
                </Box>
              )}
            </Container>

            <Container header={<Header variant="h2">Status</Header>}>
              <SpaceBetween size="s">
                <StatusIndicator type={connectionState === 'connected' ? 'success' : 'pending'}>
                  {connectionState === 'connected' ? 'Connected' : connectionState}
                </StatusIndicator>
                <SpeakingIndicator speaker={currentSpeaker} />
              </SpaceBetween>
            </Container>
          </Grid>

          {error && (
            <Alert type="warning" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Session Controls */}
          <Container>
            <Box textAlign="center" padding="s">
              <SpaceBetween direction="horizontal" size="l" alignItems="center">
                <Button
                  variant={isMuted ? 'primary' : 'normal'}
                  onClick={handleMuteToggle}
                  iconName={isMuted ? 'microphone-off' : 'microphone'}
                  data-testid="mute-button"
                  ariaLabel={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>

                <Button
                  variant="primary"
                  onClick={handleEndSessionClick}
                  iconName="close"
                  data-testid="end-session-button"
                  ariaLabel="End session"
                >
                  End Session
                </Button>

                {briefHtml && (
                  <Button
                    variant="normal"
                    onClick={() => setBriefExpanded(!briefExpanded)}
                    iconName={briefExpanded ? 'angle-right' : 'angle-left'}
                  >
                    {briefExpanded ? 'Hide Notes' : 'Show Notes'}
                  </Button>
                )}
              </SpaceBetween>
            </Box>
          </Container>
        </SpaceBetween>

        {/* Right column: Case study brief panel */}
        {briefExpanded && briefHtml && (
          <Container
            header={
              <Header
                variant="h2"
                description="Reference material for this scenario"
                actions={
                  <Button
                    variant="icon"
                    iconName="close"
                    onClick={() => setBriefExpanded(false)}
                    ariaLabel="Close notes panel"
                  />
                }
              >
                Case Study Notes
              </Header>
            }
          >
            <div
              ref={briefRef}
              className="brief-panel-content"
              style={{
                maxHeight: '700px',
                overflowY: 'auto',
                padding: '0 4px',
              }}
              dangerouslySetInnerHTML={{ __html: briefHtml }}
            />
          </Container>
        )}
      </Grid>

      {/* CSS for pulse animation and brief panel styling */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        /* Professional brief panel styling */
        .brief-panel-content {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        /* Scrollbar styling */
        .brief-panel-content::-webkit-scrollbar {
          width: 6px;
        }

        .brief-panel-content::-webkit-scrollbar-track {
          background: transparent;
        }

        .brief-panel-content::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        .brief-panel-content::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </ContentLayout>
  );
}

export default VoiceSessionPage;
